/**
 * WaterSecurityHero — the dashboard's single most important Mayor-facing
 * component. Combines Lam Takhong dam storage, the drought index, and
 * air quality into one prominent card with a clear action button.
 *
 * Never shows infinite skeleton when feeds are dark — loading only while
 * tiers are unresolved; otherwise honest "FEED GAP" empty cells.
 */

import type { DamReading, DroughtBrief, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import {
  pm25Advisory,
  DAM_COLOR,
  DAM_LABEL_TH,
  DROUGHT_COLOR,
  DROUGHT_LABEL_TH,
} from "../lib/waterSecurity";
import { Arrow } from "./Arrow";

/** A single point on the TimesFM river-level forecast horizon.
 *  Subset of ForecastPoint that's actually consumed by the hero strip.
 *  p10/p50/p90 can be null when an upstream value was missing, so we
 *  match the source shape and filter nulls in the strip component. */
export interface RiverForecastPoint {
  p10: number | null;
  p50: number | null;
  p90: number | null;
  /** ISO timestamp at the forecast horizon (offset from "now"). */
  time?: string;
}

interface Props {
  dam: DamReading | null;
  damAgeMinutes?: number | null;
  damTier?: FallbackTier;
  drought: DroughtBrief | null;
  droughtAgeMinutes?: number | null;
  droughtTier?: FallbackTier;
  /** Open-air PM2.5 (µg/m³) — Open-Meteo grid, used to give context to
   *  "drought band + good/bad air" in one glance. Optional. */
  pm25?: number | null;
  /** TimesFM river-level forecast (p10 / p50 / p90 per horizon). When
   *  present, the hero renders a tiny "where is the river going" strip
   *  below the dam/drought grid. */
  riverLevelForecast?: RiverForecastPoint[] | null;
  /** Optional: jump the map to the dam. Provided by App.tsx flyTo + lens change. */
  onViewOnMap?: () => void;
}

function SparkBar({ pct, color }: { pct: number; color: string }) {
  const safe = Math.min(100, Math.max(0, pct));
  return (
    <div
      className="water-hero-bar"
      role="img"
      aria-label={`${pct.toFixed(0)} percent`}
    >
      <div className="water-hero-bar-fill" style={{ width: `${safe}%`, background: color }} />
      <div className="water-hero-bar-marker" style={{ left: "30%" }} title="RID restriction threshold 30%" />
    </div>
  );
}

export function WaterSecurityHero({
  dam,
  damAgeMinutes,
  damTier,
  drought,
  droughtAgeMinutes,
  droughtTier,
  pm25,
  riverLevelForecast,
  onViewOnMap,
}: Props) {
  // Skeleton only while neither feed has resolved a tier yet.
  // App passes `undefined` tier while loading; once settled it passes
  // live | stale | scenario | unavailable.
  const stillLoading = !dam && !drought && damTier == null && droughtTier == null;
  if (stillLoading) {
    return (
      <div className="water-hero water-hero--loading">
        <PanelHeader
          title="น้ำ · WATER SECURITY"
          source="thaiwater.rid + dam.rain.climatology"
        />
        <div className="water-hero-skel" />
        <div className="water-hero-skel water-hero-skel--short" />
      </div>
    );
  }

  const damCol = dam ? (DAM_COLOR[dam.alertLevel] ?? "var(--data)") : "var(--text-3)";
  const damLabel = dam ? (DAM_LABEL_TH[dam.alertLevel] ?? dam.alertLevel) : "—";
  const dCol = drought ? (DROUGHT_COLOR[drought.band] ?? "var(--data)") : "var(--text-3)";
  const dLabel = drought ? (DROUGHT_LABEL_TH[drought.band] ?? drought.band) : "—";
  const air = pm25Advisory(pm25);
  const gaps: string[] = [];
  if (!dam && damTier != null) gaps.push("DAM");
  if (!drought && droughtTier != null) gaps.push("DRGT");

  return (
    <div className="water-hero" role="region" aria-label="Water security situation">
      <PanelHeader
        title="น้ำ · WATER SECURITY"
        source="thaiwater.rid + dam.rain.climatology"
        fallbackTier={!dam && !drought ? (damTier ?? droughtTier) : undefined}
      />
      {gaps.length > 0 && (
        <div className="water-hero-gap mono caption" role="status" style={{ color: "var(--warn)", marginBottom: 6 }}>
          ◆ FEED GAP · {gaps.join(" · ")} — ไม่ยืนยันสถานการณ์น้ำ
        </div>
      )}
      <div className="water-hero-grid">
        <div className="water-hero-cell">
          <div className="water-hero-cell-label mono">
            เขื่อนลำตะคอง · LAM TAKHONG
          </div>
          {dam ? (
            <>
              <div className="water-hero-big" style={{ color: damCol }}>
                {dam.pctNormal.toFixed(1)}<span className="water-hero-big-unit">%</span>
              </div>
              <SparkBar pct={dam.pctNormal} color={damCol} />
              <div className="water-hero-sub">
                <span style={{ color: damCol, fontWeight: 600 }}>{damLabel}</span>
                <span className="mono">
                  {dam.storageMcm.toFixed(1)}/{dam.normalStorageMcm.toFixed(0)} MCM
                </span>
              </div>
            </>
          ) : (
            <div className="water-hero-empty mono" style={{ color: "var(--warn)" }}>
              {damTier == null ? "loading…" : "FEED GAP · no dam data"}
            </div>
          )}
        </div>

        <div className="water-hero-cell">
          <div className="water-hero-cell-label mono">
            ภัยแล้ง · DROUGHT
          </div>
          {drought ? (
            <>
              <div className="water-hero-big" style={{ color: dCol }}>
                {drought.score}<span className="water-hero-big-unit">/100</span>
              </div>
              <SparkBar pct={drought.score} color={dCol} />
              <div className="water-hero-sub">
                <span style={{ color: dCol, fontWeight: 600 }}>{dLabel}</span>
                {drought.daysSinceRain != null && (
                  <span className="mono">
                    ฝนตกล่าสุด {drought.daysSinceRain === 0 ? "วันนี้" : `${drought.daysSinceRain} วัน`}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="water-hero-empty mono" style={{ color: "var(--warn)" }}>
              {droughtTier == null ? "loading…" : "FEED GAP · no drought data"}
            </div>
          )}

          {air && (
            <div
              className="water-hero-air"
              title={`PM2.5 ${pm25?.toFixed(1) ?? "—"} µg/m³`}
            >
              <span className="water-hero-air-label mono">PM2.5</span>
              <span className="water-hero-air-num mono" style={{ color: air.color }}>
                {pm25?.toFixed(0) ?? "—"}
              </span>
              <span className="water-hero-air-band" style={{ color: air.color }}>
                {air.label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* River-level forecast strip — TimesFM zero-shot p10/p50/p90
          over the next horizon. Tiny SVG sparkline; the "▲ +N cm" badge
          tells the operator the direction at a glance. Hidden when the
          forecast hasn't run yet (no data — not an error). */}
      {riverLevelForecast && riverLevelForecast.length >= 2 && (
        <RiverForecastStrip forecast={riverLevelForecast} />
      )}

      {onViewOnMap && (
        <div className="water-hero-actions">
          <button
            type="button"
            className="water-hero-cta"
            onClick={onViewOnMap}
            aria-label="ดูเขื่อนลำตะคองบนแผนที่ (View Lam Takhong on map)"
          >
            ดูบนแผนที่ · VIEW ON MAP <Arrow direction="up-right" />
          </button>
          <span className="water-hero-age mono">
            {damAgeMinutes != null ? `dam ${damAgeMinutes}m` : null}
            {damAgeMinutes != null && droughtAgeMinutes != null ? " · " : null}
            {droughtAgeMinutes != null ? `drought ${droughtAgeMinutes}m` : null}
          </span>
        </div>
      )}
    </div>
  );
}

/* RiverForecastStrip — small inline strip under the dam/drought grid
   that visualises the TimesFM river-level forecast. The shape is:

     RIVER · 12h          ▁▂▃▅▆▇      ▲ +14 cm in 12h

   The SVG is 12 px tall — small enough to not steal focus from the
   dam/drought numbers, big enough to read at arm's length. The
   p10/p90 confidence band is drawn as a thin grey fill; the p50 line
   is the bright stroke. The "▲ +N cm" badge is a single number that
   answers the question "is the river coming up or going down?" in
   one glance. */
function RiverForecastStrip({ forecast }: { forecast: RiverForecastPoint[] }) {
  // Drop any horizon where p10/p50/p90 are all null — TimesFM emits
  // those when the upstream recorder is dark. We need at least 2 valid
  // points to draw a meaningful sparkline.
  const clean = forecast.filter(
    (f) => f.p10 != null && f.p50 != null && f.p90 != null,
  ) as Array<{ p10: number; p50: number; p90: number; time?: string }>;
  if (clean.length < 2) return null;
  const W = 220;
  const H = 24;
  const PAD = 2;
  const p10 = clean.map((f) => f.p10);
  const p50 = clean.map((f) => f.p50);
  const p90 = clean.map((f) => f.p90);
  const all = [...p10, ...p50, ...p90];
  const minV = Math.min(...all);
  const maxV = Math.max(...all);
  const range = maxV - minV || 1;
  const denom = Math.max(1, clean.length - 1);
  const x = (i: number) => PAD + (i / denom) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - ((v - minV) / range) * (H - 2 * PAD);
  const bandTop = p90.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const bandBot = [...p10].reverse().map((v, i) => {
    const j = clean.length - 1 - i;
    return `${x(j).toFixed(1)},${y(v).toFixed(1)}`;
  }).join(" ");
  const linePts = p50.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const start = p50[0];
  const end = p50[p50.length - 1];
  const delta = end - start;
  const horizon = clean.length;
  // 0h<Arrow direction="right" />end (last point), we just call it "+N cm in Nh" using the last
  // sample. TimesFM produces horizons in fixed buckets (typically 6h
  // apart), so this is a fair label.
  const trendUp = delta > 0;
  const trendDown = delta < 0;
  const deltaLabel = `${trendUp ? "▲" : trendDown ? "▼" : "—"} ${Math.abs(delta).toFixed(1)} cm in ${horizon - 1}h`;
  const trendColor = trendUp ? "var(--warn)" : trendDown ? "var(--good)" : "var(--text-3)";
  return (
    <div className="water-hero-river" role="group" aria-label="TimesFM river level forecast">
      <span className="water-hero-river-label mono">
        แม่น้ำ · RIVER
        <span className="water-hero-river-horizon mono">{horizon - 1}h</span>
      </span>
      <svg
        className="water-hero-river-spark"
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polygon points={`${bandTop} ${bandBot}`} fill="var(--data)" fillOpacity="0.10" />
        <polyline
          points={linePts}
          fill="none"
          stroke="var(--data)"
          strokeWidth="1.4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* End-point dot — where the river is heading */}
        <circle
          cx={x(clean.length - 1).toFixed(1)}
          cy={y(end).toFixed(1)}
          r={2}
          fill="var(--data)"
        />
      </svg>
      <span className="water-hero-river-delta mono" style={{ color: trendColor }}>
        {deltaLabel}
      </span>
    </div>
  );
}
